'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["tableWidth", "width", "onChange", "onStep"],
  _excluded2 = ["className", "dropdownClassName", "disabled", "tableWidth", "dimensions", "onDimensionChange", "onBorderWidthChange"];
import PropTypes from 'prop-types';
import I18n from 'I18n';
import styled from 'styled-components';
import H5 from 'UIComponents/elements/headings/H5';
import UIFieldset from 'UIComponents/form/UIFieldset';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISelect from 'UIComponents/input/UISelect';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import StyleProtectedUIDropdown from '../style-protected/StyleProtectedUIDropdown';
import { toolbarI18nKey, textButtonStyles } from '../../constants/toolbar';
import { TableBorderStyles } from '../../constants/table';
import { isPercentage, percentageStringToNumber, pixelStringToNumber } from '../../utils/table';
import PixelStepperInput from '../table/controls/TablePopoverPixelStepperInput';
import { useDebouncedOnChange } from '../../hooks/useDebouncedOnChange';
import PercentageStepperInput from '../table/controls/TablePopoverPercentageStepperInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const i18nKey = suffix => `RichTextLib.table.toolbar.${suffix}`;
const makeEventCompatible = fn => evt => {
  const {
    value
  } = evt.target;
  fn(value);
};
const StyleControlsContainer = styled(UIFieldset).withConfig({
  displayName: "TableCellStyleControlButton__StyleControlsContainer",
  componentId: "odecio-0"
})(["min-width:250px;max-width:300px;"]);
const StyleFormLabel = styled(UIFormLabel).withConfig({
  displayName: "TableCellStyleControlButton__StyleFormLabel",
  componentId: "odecio-1"
})(["padding-top:4px;padding-bottom:4px;font-size:14px !important;font-weight:400;"]);
const StyleSelect = styled(UISelect).withConfig({
  displayName: "TableCellStyleControlButton__StyleSelect",
  componentId: "odecio-2"
})(["width:144px !important;"]);
const CellWidthStepperInput = _ref => {
  let {
      tableWidth,
      width,
      onChange,
      onStep
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (isPercentage(tableWidth)) {
    return /*#__PURE__*/_jsx(PercentageStepperInput, Object.assign({}, rest, {
      min: 2,
      max: 100,
      width: "144px",
      value: percentageStringToNumber(width),
      onChange: onChange,
      onStep: onStep
    }));
  } else {
    return /*#__PURE__*/_jsx(PixelStepperInput, Object.assign({}, rest, {
      min: 2,
      width: "144px",
      value: pixelStringToNumber(width),
      onChange: onChange,
      onStep: onStep
    }));
  }
};
const renderCellBorderControls = ({
  dropdownClassName,
  borderWidth,
  borderStyle,
  onBorderWidthChange,
  onBorderWidthStep,
  onBorderStyleChange
}) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(UIFlex, {
      alignment: "center",
      justify: "between",
      className: "m-top-4",
      children: [/*#__PURE__*/_jsx(StyleFormLabel, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('borderWidth')
        })
      }), /*#__PURE__*/_jsx(PixelStepperInput, {
        "data-test-id": "cell-border-width-input",
        min: 0,
        max: 15,
        width: "144px",
        value: pixelStringToNumber(borderWidth),
        onChange: makeEventCompatible(onBorderWidthChange),
        onStep: makeEventCompatible(onBorderWidthStep)
      })]
    }), /*#__PURE__*/_jsxs(UIFlex, {
      alignment: "center",
      justify: "between",
      className: "m-top-4",
      children: [/*#__PURE__*/_jsx(StyleFormLabel, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('borderStyle')
        })
      }), /*#__PURE__*/_jsx(StyleSelect, {
        "data-test-id": "cell-border-style-select",
        dropdownClassName: dropdownClassName,
        options: TableBorderStyles.map(style => ({
          value: style,
          text: I18n.text(i18nKey(`borderStyleOptions.${style}`))
        })),
        value: borderStyle,
        onChange: makeEventCompatible(onBorderStyleChange)
      })]
    })]
  });
};
const renderCellDimensionsControls = ({
  dimensions,
  tableWidth,
  onWidthChange,
  onHeightChange,
  onWidthStep,
  onHeightStep
}) => {
  const {
    width,
    height
  } = dimensions;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(UIFlex, {
      alignment: "center",
      justify: "between",
      className: "m-top-4",
      children: [/*#__PURE__*/_jsx(StyleFormLabel, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('cell.width')
        })
      }), /*#__PURE__*/_jsx(CellWidthStepperInput, {
        "data-test-id": "cell-width-input",
        width: width,
        tableWidth: tableWidth,
        onChange: onWidthChange,
        onStep: onWidthStep
      })]
    }), /*#__PURE__*/_jsxs(UIFlex, {
      alignment: "center",
      justify: "between",
      className: "m-top-4",
      children: [/*#__PURE__*/_jsx(StyleFormLabel, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('cell.height')
        })
      }), /*#__PURE__*/_jsx(PixelStepperInput, {
        "data-test-id": "cell-height-input",
        min: 2,
        width: "144px",
        value: pixelStringToNumber(height),
        onChange: onHeightChange,
        onStep: onHeightStep
      })]
    })]
  });
};
const propTypes = {
  className: PropTypes.string.isRequired,
  dropdownClassName: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  tableWidth: PropTypes.string.isRequired,
  dimensions: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number
  }),
  onDimensionChange: PropTypes.func.isRequired,
  onBorderWidthChange: PropTypes.func.isRequired
};
function TableCellStyleControlButton(props) {
  const {
      className,
      dropdownClassName,
      disabled,
      tableWidth,
      dimensions,
      onDimensionChange,
      onBorderWidthChange
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded2);
  const _handleDimensionChange = (dimensionType, evt) => {
    const {
      value
    } = evt.target;
    onDimensionChange({
      dimensions: Object.assign({}, dimensions, {
        [dimensionType]: value
      }),
      dimensionType
    });
  };
  const [handleBorderWidthChange, handleBorderWidthStep] = useDebouncedOnChange(onBorderWidthChange);
  const [handleDimensionChange, handleDimensionStep] = useDebouncedOnChange(_handleDimensionChange);
  return /*#__PURE__*/_jsx(StyleProtectedUIDropdown, {
    "data-test-id": "table-cell-style-control-button",
    use: "unstyled",
    buttonText: I18n.text(toolbarI18nKey('tableCellStyle')),
    buttonUse: "transparent",
    buttonClassName: className,
    autoPlacement: "horiz",
    placement: "bottom right",
    closeOnMenuClick: false,
    dropdownClassName: dropdownClassName,
    animateOnToggle: false,
    disabled: disabled,
    arrowSize: "medium",
    children: /*#__PURE__*/_jsxs(StyleControlsContainer, {
      "data-test-id": "table-cell-style-control-content",
      className: "m-all-4",
      _size: "small",
      children: [/*#__PURE__*/_jsx(H5, {
        className: "m-bottom-4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('cell.title')
        })
      }), renderCellBorderControls(Object.assign({}, rest, {
        dropdownClassName,
        onBorderWidthChange: handleBorderWidthChange,
        onBorderWidthStep: handleBorderWidthStep
      })), renderCellDimensionsControls({
        dimensions,
        tableWidth,
        onWidthChange: handleDimensionChange.bind(null, 'width'),
        onHeightChange: handleDimensionChange.bind(null, 'height'),
        onWidthStep: handleDimensionStep.bind(null, 'width'),
        onHeightStep: handleDimensionStep.bind(null, 'height')
      })]
    })
  });
}
TableCellStyleControlButton.propTypes = propTypes;
TableCellStyleControlButton.displayName = 'TableCellStyleControlButton';
const StyledTableCellStyleControlButton = styled(TableCellStyleControlButton).withConfig({
  displayName: "TableCellStyleControlButton__StyledTableCellStyleControlButton",
  componentId: "odecio-3"
})(["", ""], textButtonStyles);
export default StyledTableCellStyleControlButton;